/**
 * Shared parser for a single `question_catalog.options` element.
 *
 * The catalog stores options in a few historical shapes:
 * - `{ id, label }`            (72+ rows)
 * - `{ text, segment_paths }`  (dominant, 150+ rows — the wizard derives the id from `text`)
 * - bare strings               (a handful of "Product Feedback" rows)
 *
 * REK-1917: for a **bare string** option, `opt.id` and `opt.text` are both
 * undefined, so the old inline parser fell through to a generated `opt_<idx>`
 * id — and that placeholder (not the readable label) was stored as the user's
 * answer in `user_knowledge`, breaking answer-text analytics (violates
 * `.cursor/rules/brand-campaigns.mdc`: "use the option text as the id; do NOT
 * use generated IDs like option_1"). This helper derives a readable id from the
 * string itself so the stored selection is `neutral` rather than `opt_2`.
 *
 * The object branch is intentionally byte-for-byte identical to the previous
 * inline logic (`opt.id || opt.text?.toLowerCase()... || opt_<idx>`) so already
 * -normalized rows keep the exact same derived ids — no analytics fragmentation.
 * A DB migration also normalizes the bare-string rows to the catalog shape; this
 * helper is the defensive companion so a future bare-string option can never
 * regress to `opt_<idx>`.
 */
export interface ParsedQuestionOption {
  id: string;
  label: string;
  description?: string;
  segment_paths?: string[];
  text?: string;
}

const deriveIdFromText = (text: string): string =>
  text.toLowerCase().replace(/\s+/g, '_');

export function parseQuestionOption(opt: unknown, idx: number): ParsedQuestionOption {
  // Bare-string option (REK-1917): derive a readable id/label from the string
  // so the stored answer is the label form, not `opt_<idx>`.
  if (typeof opt === 'string') {
    return {
      id: deriveIdFromText(opt) || `opt_${idx}`,
      label: opt,
      text: opt,
    };
  }

  const o = (opt ?? {}) as Record<string, unknown>;
  const text = typeof o.text === 'string' ? o.text : undefined;
  const label = typeof o.label === 'string' ? o.label : undefined;

  return {
    id: (typeof o.id === 'string' && o.id) || (text ? deriveIdFromText(text) : '') || `opt_${idx}`,
    label: label || text || String(opt),
    description: typeof o.description === 'string' ? o.description : undefined,
    segment_paths: Array.isArray(o.segment_paths) ? (o.segment_paths as string[]) : undefined,
    text,
  };
}

/**
 * Map a derived option label to the value answering the question would
 * store (the catalog option id when the options carry explicit ids, else
 * the label verbatim). The wizard fill and the completion-% fill MUST use
 * this same helper for the DOB-derived age value: two surfaces storing
 * different forms (id vs label) evaluate `depends_on_values` differently and
 * disagree on "shown", which stalls completion. Unknown labels and missing
 * option lists fall back to the input — never undefined, never a guess.
 */
export function mapOptionLabelToId(rawOptions: unknown, label: string): string {
  if (!Array.isArray(rawOptions)) return label;
  const match = rawOptions
    .map((opt, idx) => parseQuestionOption(opt, idx))
    .find((parsed) => parsed.label === label);
  return match?.id ?? label;
}

/**
 * Project raw catalog options to `{ id, text }` pairs for age derivation:
 * the derived value resolves to the stored-form option id (see
 * `mapOptionLabelToId`). Entries that parse to an empty label are dropped.
 */
export function toAgeRangeOptionPairs(
  rawOptions: unknown,
): { id: string; text: string }[] | undefined {
  if (!Array.isArray(rawOptions)) return undefined;
  const pairs = rawOptions
    .map((opt, idx) => parseQuestionOption(opt, idx))
    .filter((parsed) => parsed.label.length > 0)
    .map((parsed) => ({ id: parsed.id, text: parsed.label }));
  return pairs.length > 0 ? pairs : undefined;
}
